import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import { SHELFY_GENRE_OPTIONS } from '../../../util/shelfyOptions';
import {
  Button,
  IconArrowHead,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import css from './Topbar.module.css';
import {
  getCurrentUserTypeRoles,
  showCreateListingLinkForUser,
  showMyShopLinkForUser,
} from '../../../util/userHelpers';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';
const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';
const DESKTOP_MENU_BUTTON_ID = 'desktopMenuButton';
const MOBILE_SEARCH_BUTTON_ID = 'mobileSearchButton';

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? [...customLinks] : [];
  return links.sort(compareGroups);
};

const internalLink = (text, href, extra = {}) => ({
  group: 'primary',
  type: 'internal',
  href,
  text,
  ...extra,
});

const marketplaceGenreLinks = () =>
  SHELFY_GENRE_OPTIONS.map(({ option, label }) =>
    internalLink(label, `/s?pub_genre=${encodeURIComponent(option)}`)
  );

export const shelfyMarketplaceNavigation = () => {
  return [
  internalLink('Marketplace', '/s', {
    sections: [
      {
        items: marketplaceGenreLinks(),
      },
      {
        label: 'More',
        items: [
          internalLink('In Search Of', '/s/iso-book'),
          internalLink('Special Editions', '/s?pub_editiontype=special,collectors,limited'),
          internalLink('Bookish Goods', '/s/collector-merch'),
        ],
      },
    ],
  }),
  internalLink('Shops', '/shops'),
  internalLink('Discover', '/trending', {
    // Discover is the one primary link whose children are also exposed in the mobile menu.
    showSectionsOnMobile: true,
    sections: [
      {
        items: [
          internalLink('Canadian Authors', '/discover/authors'),
          internalLink('Author Spotlight', '/trending/author-spotlight'),
          internalLink('Curated Reads', '/trending/curated-reads'),
        ],
      },
    ],
  }),
    internalLink('Booker’s Reading Club', '/p/shelfy-social'),
  internalLink('My Shelf', '/my-shelf'),
    internalLink('PageHop 101', '/sell', {
    sections: [
      {
        items: [
          internalLink('Seller Guide', '/p/how-to-use-shelfy/selling'),
          internalLink('Fees', '/p/how-to-use-shelfy/payments'),
          internalLink('Shipping', '/p/how-to-use-shelfy/shipping'),
          internalLink('Condition Guide', '/p/how-to-use-shelfy/condition'),
          internalLink('Getting Paid', '/p/how-to-use-shelfy/getting-paid'),
          internalLink('Seller FAQs', '/p/how-to-use-shelfy/seller-faqs'),
          internalLink('Manage Your Shop', '/profile-settings'),
          internalLink('Create a Listing', '/l/new'),
        ],
      },
    ],
  }),
];
};

export const ensureShelfyShopsLink = _customLinks => shelfyMarketplaceNavigation();

export const desktopPrimaryNavigation = links => {
  const myShelf = links.find(link => link.text === 'My Shelf');
  const linksWithoutMyShelf = links.filter(link => link.text !== 'My Shelf');
  return myShelf ? [...linksWithoutMyShelf, { ...myShelf, highlight: true }] : linksWithoutMyShelf;
};

// Resolves in-app links against route configuration
const resolveLinkConfig = (linkConfig, routeConfiguration) => {
  const isLegacySellerHubLink = ['How to Use Shelfy', 'How to Use PageHop'].includes(
    linkConfig.text
  );
  const normalizedLinkConfig = isLegacySellerHubLink
    ? { ...linkConfig, text: 'PageHop 101', type: 'internal', href: '/sell' }
      : linkConfig;
    const resolvedSections = normalizedLinkConfig.sections?.map(section => ({
      ...section,
      items: section.items?.map(item => resolveLinkConfig(item, routeConfiguration)) || [],
    }));
    const normalizedWithSections = resolvedSections
      ? { ...normalizedLinkConfig, sections: resolvedSections }
      : normalizedLinkConfig;

    if (normalizedWithSections.disabled) {
      return normalizedWithSections;
    }

    const { type, href } = normalizedWithSections;
    if (!href) {
      return normalizedWithSections;
    }
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...normalizedWithSections,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return normalizedWithSections;
};

const getResolvedCustomLinks = (customLinks, routeConfiguration, config) =>
  customLinks.map(linkConfig => resolveLinkConfig(linkConfig, routeConfiguration));

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;

const primaryNavPageForRoute = routeName => {
  if (['SearchPageWithListingType'].includes(routeName)) return 'SearchPage';
  if (['BrowseShopsPage'].includes(routeName)) return 'ShopsPage';
  if (['TrendingSubpage', 'TrendingAuthorsPage', 'AuthorsPage'].includes(routeName))
    return 'TrendingPage';
  if (['HowToUseShelfyTopicPage', 'HowToUseShelfyPage'].includes(routeName)) return 'SellerHubPage';
  return routeName;
};
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
export const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName
      ? cmsPageName
      : inboxPageName
      ? inboxPageName
      : primaryNavPageForRoute(`${found.route?.name}`);
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  const [overlay, setOverlay] = useState(null);
  const locationKey =
    location.key || `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const closeOverlay = () => setOverlay(null);
  const openOverlay = kind => setOverlay({ kind, locationKey });

  useEffect(() => {
    setOverlay(null);
  }, [locationKey]);

  useEffect(() => {
    const media = window.matchMedia?.(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`);
    const closeMobileOverlayOnDesktop = event => {
      if (!event.matches) {
        setOverlay(null);
      }
    };
    media?.addEventListener?.('change', closeMobileOverlayOnDesktop);
    return () => media?.removeEventListener?.('change', closeMobileOverlayOnDesktop);
  }, []);

  useEffect(() => {
    const closeRestoredOverlay = event => {
      if (event.persisted) setOverlay(null);
    };
    window.addEventListener('pageshow', closeRestoredOverlay);
    return () => window.removeEventListener('pageshow', closeRestoredOverlay);
  }, []);

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location || {};
      const { origin, bounds } = selectedPlace || {};
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const showMyShopLink = showMyShopLinkForUser(config, currentUser, currentUserHasListings);
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  /**
   * Determine which tab to use in the inbox link:
   * - if only provider role – sales
   * - if only customer role – orders
   * - if both roles – determine by currentUserHasListings value
   */
  const topbarInboxTab = !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';

  const { keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(
    ensureShelfyShopsLink(config.topbar?.customLinks, config)
  );
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration, config);
  const desktopCustomLinks = desktopPrimaryNavigation(customLinks);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isCurrentOverlay = overlay?.locationKey === locationKey;
  const isMobileMenuOpen = isCurrentOverlay && overlay?.kind === 'menu';
  const isMobileSearchOpen = isMobileLayout && isCurrentOverlay && overlay?.kind === 'search';

  const mobileMenu = (
    <TopbarMobileMenu
      key={isMobileMenuOpen ? 'open' : 'closed'}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      showCreateListingsLink={showCreateListingsLink}
      showMyShopLink={showMyShopLink}
      inboxTab={topbarInboxTab}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE &&
    ['SearchPage', 'SearchPageWithListingType'].includes(resolvedCurrentPage);
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      id={MOBILE_SEARCH_BUTTON_ID}
      rootClassName={css.searchMenu}
      onClick={() => openOverlay('search')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon
        className={css.searchMenuIcon}
        ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );

  const handleSkipToMainContent = e => {
    e.preventDefault();
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Focus the main content for screen readers
      mainContent.setAttribute('tabindex', '-1');
      mainContent.focus();
      // Remove tabindex after blur to avoid tabbing into it later
      mainContent.addEventListener(
        'blur',
        () => {
          mainContent.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
  };

  return (
    <div className={classes}>
      <Button onClick={handleSkipToMainContent} className={css.skipToMainContent}>
        <FormattedMessage id="Topbar.skipToMainContent" />
        <IconArrowHead direction="right" size="small" rootClassName={css.skiptoMainArrow} />
      </Button>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <nav className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          id={MOBILE_MENU_BUTTON_ID}
          rootClassName={css.menu}
          onClick={() => openOverlay('menu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon
            className={css.menuIcon}
            ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
          />
          {notificationDot}
        </Button>
        <LinkedLogo
          id="logo-topbar-mobile"
          layout={'mobile'}
          alt="PageHop Books logo"
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {mobileSearchButtonMaybe}
      </nav>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onOpenMenu={() => openOverlay('menu')}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={desktopCustomLinks}
          showSearchForm={showSearchForm}
          showCreateListingsLink={showCreateListingsLink}
          showMyShopLink={showMyShopLink}
          inboxTab={topbarInboxTab}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={closeOverlay}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={isMobileLayout ? MOBILE_MENU_BUTTON_ID : DESKTOP_MENU_BUTTON_ID}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={closeOverlay}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={MOBILE_SEARCH_BUTTON_ID}
      >
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
          />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
